import React, { useState } from 'react';
import {
  Badge, Button, ConfirmDialog, DataTable, Input, Modal, PageHead, Switch, num,
} from '../components/ui';
import useList from '../hooks/useList';
import { unitApi } from '../api';
import { useToast } from '../context/ToastContext';

const emptyForm = { code: '', label: '', plural: '', sortOrder: 0, isActive: true };

/**
 * The "sold by" list sellers pick from when listing a product.
 *
 * Held as data rather than a schema enum so a unit the trade starts using can
 * be added without a deploy.
 */
const Units = () => {
  const toast = useToast();
  const list = useList(unitApi.list, {});

  const [form, setForm] = useState(null);
  const [retire, setRetire] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.label.trim()) return toast.error('Give the unit a label');
    if (!form._id && !form.code.trim()) return toast.error('Give the unit a code');

    setBusy(true);
    try {
      if (form._id) await unitApi.update(form._id, form);
      else await unitApi.create(form);
      toast.success(form._id ? 'Unit updated' : 'Unit added');
      setForm(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const doRetire = async () => {
    setBusy(true);
    try {
      const res = await unitApi.remove(retire._id);
      toast.success(res.message);
      setRetire(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'label',
      label: 'Unit',
      render: row => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.label}</div>
          <div className="text-sm text-muted">{row.plural || row.label}</div>
        </div>
      ),
    },
    { key: 'code', label: 'Code', render: row => <code>{row.code}</code> },
    { key: 'sortOrder', label: 'Order', align: 'center' },
    {
      key: 'isActive',
      label: 'Status',
      render: row => <Badge status={row.isActive ? 'active' : 'draft'}>{row.isActive ? 'Offered' : 'Retired'}</Badge>,
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: row => (
        <div className="row-actions">
          <Button size="sm" variant="ghost" onClick={() => setForm({ ...row })}>Edit</Button>
          {row.isActive && <Button size="sm" variant="danger" onClick={() => setRetire(row)}>Retire</Button>}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Selling units"
        subtitle='What sellers can choose under "Sold by" — pieces, rolls, metres and anything else you add.'
        actions={<Button icon="＋" onClick={() => setForm({ ...emptyForm })}>New unit</Button>}
      />

      <div className="filters">
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} units` : ''}</span>
      </div>

      <DataTable columns={columns} rows={list.rows} loading={list.loading} meta={list.meta} onPage={list.setPage} />

      <Modal
        open={!!form}
        title={form?._id ? 'Edit unit' : 'New unit'}
        onClose={() => setForm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
            <Button loading={busy} onClick={submit}>{form?._id ? 'Save changes' : 'Add unit'}</Button>
          </>
        }
      >
        {form && (
          <>
            <Input
              label="Code"
              value={form.code}
              disabled={!!form._id}
              onChange={e => setForm({ ...form, code: e.target.value })}
              placeholder="roll"
              hint={
                form._id
                  ? 'The code is fixed once products are sold by this unit.'
                  : 'Lowercase, no spaces. Stored on every product that uses it.'
              }
            />
            <Input
              label="Label"
              value={form.label}
              onChange={e => setForm({ ...form, label: e.target.value })}
              placeholder="Roll"
            />
            <Input
              label="Plural"
              value={form.plural}
              onChange={e => setForm({ ...form, plural: e.target.value })}
              placeholder="Rolls"
              hint='Shown after a quantity, e.g. "20 rolls".'
            />
            <Input
              label="Sort order"
              type="number"
              value={form.sortOrder}
              onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })}
              hint="Lower numbers appear first in the seller's dropdown."
            />
            <Switch
              checked={form.isActive}
              onChange={v => setForm({ ...form, isActive: v })}
              label="Offer this unit to sellers"
            />
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={!!retire}
        title="Retire this unit?"
        message={`"${retire?.label}" will stop appearing in the seller's dropdown. Products already sold by it keep working.`}
        confirmLabel="Retire"
        loading={busy}
        onConfirm={doRetire}
        onClose={() => setRetire(null)}
      />
    </>
  );
};

export default Units;
